import React, { useState } from "react";
import { ref, push, set } from "firebase/database";
import { db } from "../firebase";
import useFile from "../hooks/useFile";
import Popup from "./Popup";

const AddPlayers = ({ gameId, basePrice }) => {
  const [player, setPlayer] = useState({
    name: "",
    role: "",
    price: basePrice || 0,
    status: "upcoming",
    image: "",
    playerNumber: "",
    mobileNo: "",
    fileId: null,
  });
  const { uploadImage, uploading, clearError, error: uploadError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleChange = (f, v) => {
    setPlayer({ ...player, [f]: v });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      setPreview(null);
      setPlayer({ ...player, image: "", fileId: null });
      return;
    }

    // If there's an existing uploaded file, delete it first
    if (player.fileId) {
      await deleteFile(player.fileId);
    }

    // Create preview first
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);

    // Upload immediately when file is selected
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      // Store the uploaded file info in player state
      setPlayer({
        ...player,
        image: result.fileUrl,
        fileId: result.fileId,
      });
    } else {
      // Show error and clear preview
      setPopup({
        isOpen: true,
        message: result.error || "Failed to upload image",
        type: "error",
      });
      setPreview(null);
      e.target.value = "";
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();

    if (!gameId) {
      setPopup({ isOpen: true, message: 'Game ID missing', type: 'error' });
      return;
    }

    // Check if image is still uploading
    if (uploading) {
      setPopup({
        isOpen: true,
        message: "Please wait for image upload to complete",
        type: "info",
      });
      return;
    }

    setLoading(true);
    const playerRef = push(ref(db, `games/${gameId}/players`));

    await set(playerRef, {
      name: player.name,
      role: player.role,
      price: player.price,
      status: "upcoming",
      soldTo: null,
      image: player.image,
      playerNumber: player.playerNumber,
      mobileNo: player.mobileNo,
      fileId: player.fileId || null,
    });

    setPopup({ isOpen: true, message: 'Player added successfully!', type: 'success' });
    setPlayer({
      name: "",
      role: "",
      price: basePrice || 0,
      image: "",
      playerNumber: "",
      mobileNo: "",
      fileId: null,
    });
    setPreview(null);
    setLoading(false);
  };

  return (
    <div className="card shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
      <div className="card-header bg-gradient text-white py-3" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <h4 className="mb-0 fw-bold">
          <i className="bi bi-person-plus-fill me-2"></i>
          Add New Player
        </h4>
      </div>

      <div className="card-body p-4">
        <form onSubmit={handleAddPlayer}>
          {/* Player Photo Upload */}
          <div className="mb-4 text-center">
            <div className="player-photo-upload-small mb-3">
              {preview ? (
                <div className="photo-preview-small">
                  <img
                    src={preview}
                    alt="Player preview"
                    className="rounded-circle"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger remove-photo-small"
                    onClick={async () => {
                      if (player.fileId) {
                        try {
                          setDeleting(true);
                          await deleteFile(player.fileId);
                        } catch (e) {
                        } finally {
                          setDeleting(false);
                        }
                      }
                      setPreview(null);
                      setPlayer({ ...player, image: "", fileId: null });
                      const input = document.getElementById("photoUploadAdd");
                      if (input) input.value = "";
                    }}
                    disabled={uploading || loading || deleting}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder-small">
                  {uploading ? (
                    <>
                      <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="mb-0 small">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-camera-fill"></i>
                      <p className="mb-0 small">Photo</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              type="file"
              id="photoUploadAdd"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading || loading || deleting}
              className="d-none"
            />
            <label
              htmlFor="photoUploadAdd"
              className={`btn btn-sm ${uploading || deleting ? "btn-secondary" : "btn-outline-primary"}`}
            >
              {uploading || deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  {preview ? "Change Photo" : "Upload Photo"}
                </>
              )}
            </label>
            <small className="d-block text-muted mt-1">Optional - Max 500KB</small>
            {uploadError && (
              <small className="d-block text-danger mt-1">
                <i className="bi bi-exclamation-circle me-1"></i>
                Please try again after some time.
              </small>
            )}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-person-fill me-2 text-primary"></i>
                Player Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter player name"
                required
                value={player.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-hash me-2 text-primary"></i>
                Jersey Number
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., 10, 7"
                value={player.playerNumber}
                onChange={(e) => handleChange("playerNumber", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-telephone-fill me-2 text-primary"></i>
                Mobile Number
              </label>
              <input
                type="tel"
                className="form-control"
                placeholder="e.g., +1234567890"
                value={player.mobileNo}
                onChange={(e) => handleChange("mobileNo", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-award-fill me-2 text-primary"></i>
                Role <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={player.role}
                required
                onChange={(e) => handleChange("role", e.target.value)}
                disabled={loading}
              >
                <option value="">Select Role</option>
                <optgroup label="Cricket">
                  <option value="Batsman">Batsman</option>
                  <option value="Bowler">Bowler</option>
                  <option value="All-rounder">All-rounder</option>
                  <option value="Keeper">Wicket Keeper</option>
                </optgroup>
                <optgroup label="Football">
                  <option value="Goalkeeper">Goalkeeper</option>
                  <option value="Defender">Defender</option>
                  <option value="Midfielder">Midfielder</option>
                  <option value="Forward">Forward</option>
                </optgroup>
                <optgroup label="Other Sports">
                  <option value="Guard">Guard (Basketball)</option>
                  <option value="Center">Center (Basketball)</option>
                  <option value="Spiker">Spiker (Volleyball)</option>
                  <option value="Setter">Setter (Volleyball)</option>
                </optgroup>
              </select>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-cash-coin me-2 text-primary"></i>
                Base Price
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter base price"
                value={player.price}
                onChange={(e) => handleChange("price", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-12 mt-4">
              <button 
                type="submit" 
                className="btn btn-success w-100 py-2 fw-semibold" 
                disabled={loading || uploading || deleting}
                style={{ fontSize: "1.05rem" }}
              >
                {loading || uploading || deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {uploading ? "Uploading..." : "Adding..."}
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle-fill me-2"></i>
                    Add Player
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', type: 'info' })}
        message={popup.message}
        type={popup.type}
      />

      <style jsx>{`
        .player-photo-upload-small {
          position: relative;
          width: 100px;
          height: 100px;
          margin: 0 auto;
        }

        .photo-placeholder-small {
          width: 100px;
          height: 100px;
          border: 2px dashed #dee2e6;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .photo-placeholder-small:hover {
          border-color: #667eea;
          background: #f0f2ff;
        }

        .photo-placeholder-small i {
          font-size: 2rem;
          color: #6c757d;
          margin-bottom: 5px;
        }

        .photo-placeholder-small p {
          color: #6c757d;
        }

        [data-theme="dark"] .photo-placeholder-small {
          background: #2d2d44;
          border-color: #4a4a6a;
        }

        [data-theme="dark"] .photo-placeholder-small:hover {
          border-color: #667eea;
          background: #3d3d5c;
        }

        [data-theme="dark"] .photo-placeholder-small i {
          color: #a0a0a0;
        }

        [data-theme="dark"] .photo-placeholder-small p {
          color: #a0a0a0;
        }

        .photo-preview-small {
          position: relative;
          width: 100px;
          height: 100px;
        }

        .photo-preview-small img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: 3px solid #667eea;
          box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);
        }

        .remove-photo-small {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .bg-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default AddPlayers;
